import React, { useState, useEffect, useRef } from 'react';
import { HardDrive, X, Plus, RefreshCw, Film } from 'lucide-react';
import api from '../api';
import { useToast } from '../context/ToastContext';

const isVid = (n) => /\.(mp4|mov|webm|m4v|avi)$/i.test(n || '');

/**
 * Modale di gestione media di una riga PED (storia/reel/post), via Client Vault.
 * Props:
 *  - row: la riga PED (con media_url, id, id_post) o null (chiusa)
 *  - clienteId: id cliente
 *  - onPatch(row, { media_url }): applica l'aggiornamento (es. patchRiga/patchQuando)
 *  - onClose(): chiude
 * I video (storie/reel) passano dal Vault (fino a 2GB) → link pubblico per PostProxy.
 */
export default function MediaManager({ row, clienteId, onPatch, onClose }) {
  const { success, error } = useToast();
  const [suggest, setSuggest] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setSuggest([]);
    const code = (row?.id_post || '').trim();
    if (row && clienteId && code) {
      api.get(`/vault/${clienteId}/handle`, { params: { code }, silent: true })
        .then(r => setSuggest(r.data.entries || []))
        .catch(() => {});
    }
  }, [row && row.id, row && row.id_post, clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!row) return null;
  const media = Array.isArray(row.media_url) ? row.media_url : [];

  const attach = (url) => { if (!media.includes(url)) onPatch(row, { media_url: [...media, url] }); };
  const detach = (i) => onPatch(row, { media_url: media.filter((_, idx) => idx !== i) });

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    const folder = `PED/${row.id_post || String(row.id).slice(0, 8)}`;
    const added = [];
    for (const f of files) {
      if (f.size > 2 * 1024 * 1024 * 1024) { error(`${f.name}: oltre 2GB, saltato`); continue; }
      try {
        const fd = new FormData();
        fd.append('path', folder);
        fd.append('file', f);
        const up = await api.post(`/vault/${clienteId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, silent: true });
        if (up.data?.url) added.push(window.location.origin + up.data.url);
      } catch { error(`Upload di ${f.name} fallito`); }
    }
    // un solo onPatch con tutti i nuovi URL (evita race sull'upload multiplo)
    const fresh = added.filter(u => !media.includes(u));
    if (fresh.length) { onPatch(row, { media_url: [...media, ...fresh] }); success(`${fresh.length} media aggiunti`); }
    setBusy(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 800, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, width: 'min(560px, 100%)', maxHeight: '88vh', overflowY: 'auto', padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
            <HardDrive size={16} color="var(--cyan)" /> Media del contenuto
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}><X size={20} /></button>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 14 }}>{row.titolo_post || 'Contenuto'}{row.id_post ? ` · ${row.id_post}` : ''}</div>

        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>Media caricati</div>
        {media.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-dim)', marginBottom: 14 }}>Nessun media. Un contenuto <b>Auto</b> senza media non viene pubblicato.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            {media.map((u, i) => (
              <div key={i} style={{ position: 'relative', width: 84, height: 84, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg3)' }}>
                {isVid(u) ? <video src={u} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <img src={u} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                <button onClick={() => detach(i)} title="Rimuovi" style={{ position: 'absolute', top: 3, right: 3, width: 20, height: 20, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={11} /></button>
              </div>
            ))}
          </div>
        )}

        <button onClick={() => inputRef.current?.click()} disabled={busy}
          style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, padding: '0 16px', height: 40, borderRadius: 9, border: 'none', background: 'var(--cyan)', color: 'var(--on-accent)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, marginBottom: 6 }}>
          {busy ? <RefreshCw size={15} style={{ animation: 'eden-spin 0.7s linear infinite' }} /> : <Plus size={15} />} Carica media (nel Vault)
        </button>
        <input ref={inputRef} type="file" multiple accept="image/*,video/*" hidden onChange={e => { upload(e.target.files); e.target.value = ''; }} />
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 14 }}>I video vanno nel Client Vault (fino a 2GB) e ottengono il link pubblico per PostProxy.</div>

        {row.id_post && suggest.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>Dal Vault · handle {row.id_post}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {suggest.map(en => {
                const abs = window.location.origin + en.url;
                const added = media.includes(abs);
                return (
                  <button key={en.path} onClick={() => attach(abs)} disabled={added} title={en.name}
                    style={{ position: 'relative', width: 72, height: 72, borderRadius: 9, overflow: 'hidden', border: `1px solid ${added ? 'var(--green)' : 'var(--border)'}`, background: 'var(--bg3)', cursor: added ? 'default' : 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isVid(en.name) ? <Film size={18} color="var(--purple)" /> : <img src={en.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: added ? 'var(--green)' : 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '1px 0', textAlign: 'center' }}>{added ? '✓' : '+ aggiungi'}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
